"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { track } from "@/lib/analytics";

type Props = {
  title: string;
  description: string;
  tags: string[];
  url: string;
};

export default function SharePost({ title, description, tags, url }: Props) {
  const t = useTranslations("share");
  const [copied, setCopied] = useState(false);
  const [captionCopied, setCaptionCopied] = useState<"facebook" | "linkedin" | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const captionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (captionTimerRef.current) clearTimeout(captionTimerRef.current);
  }, []);

  const hashtags = tags.slice(0, 4).map((t) => `#${t}`).join(" ");
  const caption = `${title}\n\n${description}${hashtags ? `\n\n${hashtags}` : ""}`;

  const tweetUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;

  function shareWithCaption(platform: "facebook" | "linkedin") {
    track("share", { method: platform, content_type: "blog_post", item_id: url });
    const shareUrl = platform === "facebook" ? facebookUrl : linkedInUrl;
    window.open(shareUrl, "_blank", "noopener,noreferrer");
    navigator.clipboard.writeText(caption).then(() => {
      setCaptionCopied(platform);
      if (captionTimerRef.current) clearTimeout(captionTimerRef.current);
      captionTimerRef.current = setTimeout(() => setCaptionCopied(null), 2000);
    }).catch(() => {});
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      track("copy_link", { item_id: url });
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard access denied
    }
  }

  const btnClass =
    "inline-flex items-center justify-center min-h-[44px] gap-2 border border-[var(--accent)]/30 text-[var(--muted)] hover:border-[var(--accent)] hover:text-[var(--accent-text)] text-xs px-4 py-1.5 rounded-full transition-colors cursor-pointer";

  return (
    <div className="border-t border-white/5 mt-16 pt-10">
      <h2 className="font-mono text-[var(--muted)] text-xs uppercase tracking-widest font-normal">
        {t("heading")}
      </h2>
      <div className="flex items-center gap-3 mt-4 flex-wrap">
        {/* X (Twitter) */}
        <a
          href={tweetUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("share", { method: "twitter", content_type: "blog_post", item_id: url })}
          className={btnClass}
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.254 5.622 5.91-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
          {t("tweet")}
        </a>

        {/* LinkedIn — copies caption + opens share page */}
        <button
          onClick={() => shareWithCaption("linkedin")}
          className={`${btnClass} min-w-[148px] justify-center`}
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
          {captionCopied === "linkedin" ? t("captionCopied") : t("linkedin")}
        </button>

        {/* Facebook — copies caption + opens share page */}
        <button
          onClick={() => shareWithCaption("facebook")}
          className={`${btnClass} min-w-[152px] justify-center`}
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.532-4.697 1.313 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.885v2.269h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
          </svg>
          {captionCopied === "facebook" ? t("captionCopied") : t("facebook")}
        </button>

        {/* Copy link */}
        <button
          onClick={copyLink}
          className={`${btnClass} min-w-[100px] justify-center`}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
            />
          </svg>
          {copied ? t("copied") : t("copy")}
        </button>
      </div>
    </div>
  );
}
